import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebaseconfig";
import { useGlobal } from "../App";
import { getBestCategoryDiscount } from "../utils/discounts";


const ITEMS_PER_PAGE = 24;

export default function CategoryPage() {
  const { name, subcategory } = useParams();
  const { products, coupons, showNotification } = useGlobal();
  const user = auth.currentUser;
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = Math.max(1, Number(searchParams.get("page")) || 1);
  const initialSort = searchParams.get("sort") || "default";

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [filterBy, setFilterBy] = useState(initialSort); // default, price-low, price-high, newest, in-stock, out-of-stock

  const categoryName = name?.toLowerCase();
  const decodedSubcategory = (() => {
    if (!subcategory) return "";
    try {
      return decodeURIComponent(subcategory).trim();
    } catch {
      return subcategory.trim();
    }
  })();
  const normalizedSubcategory = decodedSubcategory.toLowerCase();

  /* ================= Filter ================= */
  let filteredProducts = products.filter(
    (p) => p.category?.toLowerCase() === categoryName
  );

  if (categoryName === "frames" && normalizedSubcategory) {
    filteredProducts = filteredProducts.filter(
      (product) => (product.subdomain || "").trim().toLowerCase() === normalizedSubcategory
    );
  }

  filteredProducts = filteredProducts.filter((product) => {
    if (filterBy === "in-stock") return product.inStock !== false;
    if (filterBy === "out-of-stock") return product.inStock === false;
    return true;
  });

  /* ================= Sort ================= */
  const sortProducts = (productsToSort) => {
    const sorted = [...productsToSort];
    
    const activeSort = ["price-low", "price-high", "newest"].includes(filterBy)
      ? filterBy
      : "default";

    switch(activeSort) {
      case "price-low":
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case "price-high":
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "newest":
        return sorted.reverse(); // assumes last added is newest
      default:
        return sorted;
    }
  };

  filteredProducts = sortProducts(filteredProducts);

  /* ================= Pagination ================= */
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleProducts = filteredProducts.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const updateUrlParams = useCallback((page, sort = filterBy) => {
    const nextPage = Math.max(1, page);
    const params = new URLSearchParams(searchParams);
    params.set("page", String(nextPage));

    if (sort && sort !== "default") {
      params.set("sort", sort);
    } else {
      params.delete("sort");
    }

    setSearchParams(params);
  }, [filterBy, searchParams, setSearchParams]);

  const goToPage = (page) => {
    const targetPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(targetPage);
    updateUrlParams(targetPage);
  };

  /* ================= Utils ================= */
  const slugify = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const getFramePrice = (product) => {
    if (product.smallframeprice) return product.smallframeprice;
    return product.price;
  };

  const getDisplayPrice = (price, category) => {
    const result = getBestCategoryDiscount(price, coupons, category);
    return result;
  };

  /* ================= Add To Cart (Stickers only) ================= */
  const addToCart = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (product.inStock === false) {
      alert("This product is out of stock");
      return;
    }

    // ✅ Store only productId, quantity, size (Best Practice)
    const cartItem = {
      productId: product.id,
      quantity: 1,
      size: null,
    };

    if (user?.email) {
      const q = query(
        collection(db, "Users"),
        where("User", "==", user.email)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return;

      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();
      const userRef = doc(db, "Users", userDoc.id);

      let updatedCart = Array.isArray(userData.cart) ? [...userData.cart] : [];

      const existingIndex = updatedCart.findIndex(
        item =>
          item.productId === cartItem.productId &&
          item.size === cartItem.size
      );

      if (existingIndex !== -1) {
        updatedCart[existingIndex].quantity += cartItem.quantity;
      } else {
        updatedCart.push(cartItem);
      }

      await updateDoc(userRef, {
        cart: updatedCart,
      });
    } else {
      let cartLS = JSON.parse(localStorage.getItem("cart")) || [];

      const existingIndex = cartLS.findIndex(
        item =>
          item.productId === cartItem.productId &&
          item.size === cartItem.size
      );

      if (existingIndex !== -1) {
        cartLS[existingIndex].quantity += cartItem.quantity;
      } else {
        cartLS.push(cartItem);
      }

      localStorage.setItem("cart", JSON.stringify(cartLS));
    }

    if (showNotification) {
      showNotification("Item added to cart");
    }
  };

  // Click tracking function
  const trackProductClick = async (product) => {
    try {
      const clickData = {
        productName: product.name,
        productId: product.id,
        username: user ? user.email : "Anonymous",
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      };

      await addDoc(collection(db, "clicks"), clickData);
      console.log("Click tracked:", clickData);
    } catch (error) {
      console.error("Error tracking click:", error);
    }
  };

  /* ================= Scroll ================= */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    const pageFromUrl = Math.max(1, Number(searchParams.get("page")) || 1);
    const sortFromUrl = searchParams.get("sort") || "default";

    if (pageFromUrl !== currentPage) {
      setCurrentPage(pageFromUrl);
    }

    if (sortFromUrl !== filterBy) {
      setFilterBy(sortFromUrl);
    }
  }, [searchParams, currentPage, filterBy]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      updateUrlParams(totalPages, filterBy);
    }
  }, [currentPage, totalPages, filterBy, updateUrlParams]);

  return (
    <>
     

      <div className="maxw" style={{ padding: "120px 20px" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ textTransform: "capitalize" }}>
            {normalizedSubcategory ? `${categoryName} / ${decodedSubcategory}` : categoryName}
          </h1>
          
          <select 
            className="form-select" 
            style={{ width: "220px" }}
            value={filterBy}
            onChange={(e) => {
              const newSort = e.target.value;
              setFilterBy(newSort);
              setCurrentPage(1);
              updateUrlParams(1, newSort);
            }}
          >
            <option value="default">Default</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest</option>
            <option value="in-stock">In stock only</option>
            <option value="out-of-stock">Out of stock only</option>
          </select>
        </div>

        {/* Products */}
        <div className="products-grid mt-4">
          {visibleProducts.length > 0 ? (
            visibleProducts.map((product) => {
              const isFrame =
                product.category?.toLowerCase() === "frames";

              return (
                <div 
                  className="product" 
                  key={product.id} 
                  data-product-name={product.name} 
                  data-product-id={product.id}
                >
                  {/* Image + title clickable */}
                  <Link
                    to={`/products/${slugify(product.name)}`}
                    className="navlink"
                    onClick={(e) => {
                      trackProductClick(product);
                      // Don't prevent default - allow navigation
                    }}
                  >
                    <div className="imgcontainer">
                      <img src={product.imageUrl || product.image} alt={product.name} />
                    </div>

                    <h5>{product.name}</h5>
                  </Link>
     
  <p>
            {product.category === 'Frames' ? `From ${product.smallframeprice} LE ` : `${product.price} LE`}

             {product.inStock === false && (
                    <span className="badge bg-danger ms-3" style={{ marginBottom: "6px" }}>
                      Out of stock
                    </span>
                  )}
          </p>

                  {product.inStock === false ? (
                    <button
                      className="btn w-100 mt-2 fw-bold"
                      style={{
                        backgroundColor: "#1f2730",
                        color: "#f2d5bd",
                        border: "none",
                        opacity: 0.7,
                      }}
                      disabled
                    >
                      Out of stock
                    </button>
                  ) : isFrame ? (
                    <Link
                      to={`/products/${slugify(product.name)}`}
                      className="btn w-100 mt-2 fw-bold"
                      style={{
                        backgroundColor: "#1f2730",
                        color: "#f2d5bd",
                        border: "none"
                      }}
                      onClick={(e) => {
                        trackProductClick(product);
                        // Don't prevent default - allow navigation
                      }}
                    >
                      Choose size
                    </Link>
                  ) : (
                    <button
                      className="btn w-100 mt-2 fw-bold"
                      style={{
                        backgroundColor: "#1f2730",
                        color: "#f2d5bd",
                        border: "none"
                      }}
                      onClick={(e) => {
                        trackProductClick(product);
                        addToCart(product, e);
                      }}
                    >
                      Add to cart
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-5">
              <h3>No products found</h3>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center gap-2 mt-4">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`btn ${
                    page === currentPage
                      ? "btn-dark"
                      : "btn-outline-dark"
                  }`}
                >
                  {page}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </>
  );
}
